/**
 * newCopy entry for UKG_WRITE_FAILED.
 *
 * One `IssueKind` covers every UKG write failure; the backend records which
 * one in the message's leading code (`UKG_VALIDATION: …`). The copy branches
 * on that code, and on whether an automation action or a subtask's data
 * destination queued the write.
 */
import { PARTICIPANT_ACTOR_KEY } from '@dadohr/shared/domainTypes/experience';
import React, { ReactNode } from 'react';
import type { UkgWriteFailedErrorWithData } from '../../InstanceProblems';
import { findParentTaskTemplate } from '../taskTemplateLookup';
import { CopyPack, Issue, IssueCopyContext, IssueKind } from '../types';
import {
  automationLink,
  ctxOf,
  dataOfFamily,
  genericRetrySupportSteps,
  HELP_ARTICLES,
  HelpArticle,
  personLabel,
  retryStep,
  SupportEmail,
  taskLink,
  tt
} from './shared';

const dataOf = dataOfFamily('INSTANCE_SUBTASK');
const ukgOf = (group: Issue[]): UkgWriteFailedErrorWithData => dataOf(group);

export type UkgErrorCode =
  | 'NO_UKG_EMPLOYEE_ID'
  | 'WRONG_TYPE_FOR_UKG_EMPLOYEE_ID'
  | 'CONFIGURATION'
  | 'FIELD_NOT_DEFINED'
  | 'INVALID_UKG_FIELD'
  | 'NO_UKG_COMPANY_CODE'
  | 'UKG_CLIENT_ERROR'
  | 'UKG_UNKNOWN_SERVICE'
  | 'UKG_SERVICE_ACCESS_DENIED'
  | 'UKG_ENTITY_NOT_FOUND'
  | 'UKG_VALIDATION'
  | 'UKG_SOAP_FAULT'
  | 'UNKNOWN';

/** Message prefixes the worker writes, longest-first where one contains another. */
const CODE_PREFIXES: [string, UkgErrorCode][] = [
  ['WRONG_TYPE_FOR_UKG_EMPLOYEE_ID', 'WRONG_TYPE_FOR_UKG_EMPLOYEE_ID'],
  ['NO_UKG_EMPLOYEE_ID', 'NO_UKG_EMPLOYEE_ID'],
  ['CONFIG_NOT_FOUND', 'CONFIGURATION'],
  ['INCOMPLETE_CONFIGURATION', 'CONFIGURATION'],
  ['FIELD_NOT_DEFINED', 'FIELD_NOT_DEFINED'],
  ['INVALID_UKG_FIELD', 'INVALID_UKG_FIELD'],
  ['NO_UKG_COMPANY_CODE', 'NO_UKG_COMPANY_CODE'],
  ['UKG_CLIENT_ERROR', 'UKG_CLIENT_ERROR'],
  ['UKG_UNKNOWN_SERVICE', 'UKG_UNKNOWN_SERVICE'],
  ['UKG_SERVICE_ACCESS_DENIED', 'UKG_SERVICE_ACCESS_DENIED'],
  ['UKG_ENTITY_NOT_FOUND', 'UKG_ENTITY_NOT_FOUND'],
  ['UKG_VALIDATION', 'UKG_VALIDATION'],
  ['UKG_SOAP_FAULT', 'UKG_SOAP_FAULT']
];

export const ukgErrorCodeOf = (message: string | undefined): UkgErrorCode =>
  CODE_PREFIXES.find(([prefix]) => (message || '').startsWith(prefix))?.[1] ?? 'UNKNOWN';

/** The text after the code, i.e. the detail UKG itself returned. */
export const ukgDetailOf = (message: string | undefined): string =>
  (message || '').replace(/^[A-Z_]+(?: \([^)]*\))?:\s*/, '');

const fromAutomation = (d: UkgWriteFailedErrorWithData): boolean => !!d.actionTplId;

const opener = (c: IssueCopyContext, d: UkgWriteFailedErrorWithData): string =>
  fromAutomation(d)
    ? tt(c, 'When the automation(s) listed below ran,')
    : tt(c, 'When the task(s) listed below completed,');

/** "the automation" / "the data destination", for the misconfiguration sentence. */
const configuredOn = (c: IssueCopyContext, d: UkgWriteFailedErrorWithData): string =>
  fromAutomation(d) ? tt(c, 'the automation') : tt(c, 'the data destination');

const itemLink = (c: IssueCopyContext, d: UkgWriteFailedErrorWithData): ReactNode =>
  fromAutomation(d)
    ? automationLink(c, d.automationTplId, tt(c, 'automation'))
    : taskLink(c, findParentTaskTemplate(d.tpl, d.subtaskTplId)?.id, tt(c, "subtask's data destination"));

const participant = (c: IssueCopyContext, d: UkgWriteFailedErrorWithData): string =>
  personLabel(c, PARTICIPANT_ACTOR_KEY, d.instance);

const addEmployeeIdSteps = (c: IssueCopyContext): ReactNode => (
  <ul>
    <li>
      {tt(c, 'Importing from an external system')} <HelpArticle href={HELP_ARTICLES.import} />
    </li>
    <li>
      {tt(c, 'Directly editing the UKG Employee ID field on their user profile')}{' '}
      <HelpArticle href={HELP_ARTICLES.editUserProfile} />
    </li>
  </ul>
);

const updateEmployeeIdSteps = (c: IssueCopyContext): ReactNode => (
  <ul>
    <li>
      {tt(
        c,
        'Deleting the current value in this field by directly editing their user profile, then importing from an external system'
      )}{' '}
      <HelpArticle href={HELP_ARTICLES.import} />
    </li>
    <li>
      {tt(c, 'Directly editing the UKG Employee ID field on their user profile')}{' '}
      <HelpArticle href={HELP_ARTICLES.editUserProfile} />
    </li>
  </ul>
);

/** The "check they're in UKG and their id is right" pair that opens the
 * fixes for every lookup failure. */
const confirmEmployeeIdSteps = (c: IssueCopyContext): ReactNode => (
  <>
    <li>
      {tt(c, 'Confirm that they are a user in UKG, and their UKG Employee ID listed on their Dado profile is correct.')}
    </li>
    <li>
      {tt(c, 'If you need to update their UKG Employee ID, do so by either:')}
      {updateEmployeeIdSteps(c)}
    </li>
  </>
);

const contactSupport = (c: IssueCopyContext, lead: string): ReactNode => (
  <li>
    {tt(c, lead)} <SupportEmail />{' '}
    {tt(
      c,
      "who can help diagnose the underlying cause of this error. It's helpful if you include a screenshot of this issue and a link to this page."
    )}
  </li>
);

const reconnectStep = (c: IssueCopyContext, lead: string): ReactNode => (
  <li>
    {tt(c, lead)} <HelpArticle href={HELP_ARTICLES.ukgIntegration} />
  </li>
);

type RowCopy = {
  summary: (c: IssueCopyContext, d: UkgWriteFailedErrorWithData) => string;
  whatCausedIt: (c: IssueCopyContext, d: UkgWriteFailedErrorWithData) => ReactNode;
  howToFixIt: (c: IssueCopyContext, d: UkgWriteFailedErrorWithData) => ReactNode;
};

const generic: RowCopy = {
  summary: (c) => tt(c, 'Data couldn’t be synced to UKG'),
  whatCausedIt: (c, d) => (
    <>
      {opener(c, d)} {tt(c, 'an unknown issue prevented Dado from syncing data to UKG.')}
    </>
  ),
  howToFixIt: () => genericRetrySupportSteps()
};

const misconfigured = (
  whatCausedIt: RowCopy['whatCausedIt']
): RowCopy => ({
  summary: (c) => tt(c, 'Data couldn’t be synced to UKG: misconfigured field(s)'),
  whatCausedIt,
  howToFixIt: (c) => <ul>{contactSupport(c, 'Contact')}</ul>
});

const ROWS: Record<UkgErrorCode, RowCopy> = {
  NO_UKG_EMPLOYEE_ID: {
    summary: (c) => tt(c, 'Data couldn’t be synced to UKG: missing UKG ID'),
    whatCausedIt: (c, d) => (
      <>
        {opener(c, d)}{' '}
        {tt(
          c,
          '{{ fullName }} had no UKG Employee ID in their Dado profile. Dado needs this ID to be able to find their user record in UKG and update their data. This can happen if they are not yet a user in UKG.',
          { fullName: participant(c, d) }
        )}
      </>
    ),
    howToFixIt: (c) => (
      <ul>
        <li>
          {tt(c, 'If they are')} <strong>{tt(c, 'not yet')}</strong> {tt(c, 'a user in UKG, wait until they are and click')}{' '}
          <strong>{tt(c, 'retry')}</strong>
        </li>
        <li>
          {tt(c, 'If they are already a UKG user, add their UKG Employee ID to Dado by either:')}
          {addEmployeeIdSteps(c)}
        </li>
        {retryStep()}
      </ul>
    )
  },
  WRONG_TYPE_FOR_UKG_EMPLOYEE_ID: {
    summary: (c) => tt(c, 'Data couldn’t be synced to UKG: invalid UKG ID'),
    whatCausedIt: (c, d) => (
      <>
        {opener(c, d)}{' '}
        {tt(
          c,
          '{{ fullName }} had an invalid UKG Employee ID in their Dado profile. Dado needs this ID to be able to find their user record in UKG and update their data.',
          { fullName: participant(c, d) }
        )}
      </>
    ),
    howToFixIt: (c) => (
      <ul>
        <li>
          {tt(c, 'Update their UKG Employee ID by either:')}
          {updateEmployeeIdSteps(c)}
        </li>
        {retryStep()}
      </ul>
    )
  },
  CONFIGURATION: {
    summary: (c) => tt(c, 'Data couldn’t be synced to UKG: integration incomplete'),
    whatCausedIt: (c, d) => (
      <>
        {opener(c, d)}{' '}
        {tt(
          c,
          'the Dado<>UKG integration was not active, or was missing some key credentials. This prevented Dado from syncing data to UKG.'
        )}
      </>
    ),
    howToFixIt: (c) => (
      <ul>
        {reconnectStep(c, 'Reconnect the UKG integration')}
        {retryStep()}
      </ul>
    )
  },
  FIELD_NOT_DEFINED: {
    summary: (c) => tt(c, 'Data couldn’t be synced to UKG: no UKG field set'),
    whatCausedIt: (c, d) => (
      <>
        {opener(c, d)}{' '}
        {fromAutomation(d)
          ? tt(c, "the automation didn't specify which UKG field Dado should update.")
          : tt(c, "the subtask's data destination didn't specify which UKG field Dado should update.")}
      </>
    ),
    howToFixIt: (c, d) => (
      <ul>
        <li>
          {tt(c, 'Update the')} {itemLink(c, d)} {tt(c, 'to specify the UKG field Dado should update')}
        </li>
        {retryStep()}
      </ul>
    )
  },
  INVALID_UKG_FIELD: misconfigured((c, d) => (
    <>
      {opener(c, d)} {tt(c, 'Dado could not find the')} <strong>{d.objectName}</strong>{' '}
      {tt(c, 'field in UKG, so could not update it. This is most likely due to a misconfiguration on {{ configuredOn }}.', {
        configuredOn: configuredOn(c, d)
      })}
    </>
  )),
  UKG_UNKNOWN_SERVICE: misconfigured((c, d) => (
    <>
      {opener(c, d)}{' '}
      {tt(
        c,
        "Dado couldn't find the area of the user's profile that needed to be updated. This is most likely due to a misconfiguration on {{ configuredOn }}.",
        { configuredOn: configuredOn(c, d) }
      )}
    </>
  )),
  NO_UKG_COMPANY_CODE: {
    summary: (c, d) =>
      tt(c, "Data couldn’t be synced to UKG: couldn't find {{ fullName }}'s UKG user", { fullName: participant(c, d) }),
    whatCausedIt: (c, d) => (
      <>
        {opener(c, d)}{' '}
        {tt(
          c,
          "Dado could not find {{ fullName }}'s user in UKG. This can happen if the UKG Employee ID on their Dado profile is incorrect, or they are not (yet) in UKG.",
          { fullName: participant(c, d) }
        )}
      </>
    ),
    howToFixIt: (c) => (
      <ul>
        {confirmEmployeeIdSteps(c)}
        {retryStep()}
      </ul>
    )
  },
  UKG_CLIENT_ERROR: {
    summary: (c) => tt(c, 'Data couldn’t be synced to UKG: issues connecting to UKG'),
    whatCausedIt: (c, d) => (
      <>
        {opener(c, d)}{' '}
        {tt(
          c,
          'Dado had issues connecting to the UKG API. This is usually caused by problems with the Dado<>UKG integration, or an invalid UKG Employee ID.'
        )}
      </>
    ),
    howToFixIt: (c) => (
      <ul>
        {confirmEmployeeIdSteps(c)}
        {retryStep()}
        {reconnectStep(c, "If this doesn't work, reconnect the UKG integration")}
        {retryStep()}
        {contactSupport(c, "If this still doesn't work, contact")}
      </ul>
    )
  },
  UKG_SERVICE_ACCESS_DENIED: {
    summary: (c) => tt(c, 'Data couldn’t be synced to UKG: integration is missing permissions'),
    whatCausedIt: (c, d) => (
      <>
        {opener(c, d)} {tt(c, 'the Dado<>UKG integration was lacking the permissions to sync data to the')}{' '}
        <strong>{d.objectName}</strong> {tt(c, 'section of the user profile.')}
      </>
    ),
    howToFixIt: (c, d) => (
      <ul>
        <li>
          {tt(c, 'Ask your UKG administrator to grant the')} <strong>{d.objectName}</strong>{' '}
          {tt(c, 'web-service role (View + Update/Add) to the service account.')}
        </li>
        {retryStep()}
      </ul>
    )
  },
  UKG_ENTITY_NOT_FOUND: {
    summary: (c, d) =>
      tt(c, "Data couldn’t be synced to UKG: {{ fullName }}'s {{ objectName }} section could not be found in UKG", {
        fullName: participant(c, d),
        objectName: d.objectName
      }),
    whatCausedIt: (c, d) => (
      <>
        {opener(c, d)} {tt(c, 'Dado could not find the')} <strong>{d.objectName}</strong> {tt(c, 'section of')}{' '}
        <strong>{participant(c, d)}</strong>
        {tt(
          c,
          "'s UKG profile. This can happen if they are not in UKG, are listed under a different company, or that section hasn't been set up on their profile."
        )}
      </>
    ),
    howToFixIt: (c, d) => (
      <ul>
        {confirmEmployeeIdSteps(c)}
        <li>
          {tt(c, 'Confirm that the {{ objectName }} section is set up on their UKG profile.', { objectName: d.objectName })}
        </li>
        {retryStep()}
      </ul>
    )
  },
  UKG_VALIDATION: {
    summary: (c) => tt(c, 'Data couldn’t be synced to UKG'),
    whatCausedIt: (c, d) => (
      <>
        {opener(c, d)} {tt(c, 'UKG rejected the data Dado sent. UKG provided the following details:')}{' '}
        <em>{ukgDetailOf(d.errorMessage)}</em>
      </>
    ),
    howToFixIt: (c, d) => {
      return (
        <ul>
          <li>
            {tt(c, 'Check the')} {itemLink(c, d)} {tt(c, 'to see what value(s) are being sent for these field(s)')}
            {fromAutomation(d) && (
              <ul>
                <li>
                  {tt(
                    c,
                    'If the value sent by Dado is coming from the user data, you can check the user profile of each participant listed below to see the exact value'
                  )}
                </li>
              </ul>
            )}
          </li>
          <li>
            {tt(c, 'Review the requirements for these field(s) and any required companion fields in UKG, and update the')}{' '}
            {itemLink(c, d)} {tt(c, 'in Dado to match these requirements')}
          </li>
          {contactSupport(c, "If this still doesn't work, contact")}
        </ul>
      );
    }
  },
  UKG_SOAP_FAULT: {
    summary: (c) => tt(c, 'Data couldn’t be synced to UKG'),
    whatCausedIt: (c, d) => (
      <>
        {opener(c, d)}{' '}
        {tt(c, 'an unexpected issue prevented Dado from syncing data to UKG. UKG provided the following details:')}{' '}
        <em>{ukgDetailOf(d.errorMessage)}</em>
      </>
    ),
    howToFixIt: () => genericRetrySupportSteps()
  },
  UNKNOWN: generic
};

const rowOf = (group: Issue[]): RowCopy => ROWS[ukgErrorCodeOf(ukgOf(group).errorMessage)];

export const ukgWriteFailedCopy: CopyPack[IssueKind.UKG_WRITE_FAILED] = {
  category: 'Data sync failed',
  summary: (g) => rowOf(g).summary(ctxOf(g), ukgOf(g)),
  whatCausedIt: (g) => rowOf(g).whatCausedIt(ctxOf(g), ukgOf(g)),
  howToFixIt: (g) => rowOf(g).howToFixIt(ctxOf(g), ukgOf(g))
};
